import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { StorageService } from 'src/app/services/storage/storage.service';

@Component({
  selector: 'app-dashboard-approval-documents',
  templateUrl: './dashboard-approval-documents.component.html',
  styleUrls: ['./dashboard-approval-documents.component.scss'],
})
export class DashboardApprovalDocumentsComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() approval: any;
  @Input() comparingVersions: boolean = false;

  clearAttachments: boolean = false;

  mainData: Contracts[] = [];
  mainDocuments: any[] = [];
  mainDocumentsRaw: any[] = [];
  mainLoading = false;

  comparingData: Contracts[] = [];
  comparingDocuments: any[] = [];
  comparingDocumentsRaw: any[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  data: Contracts[] = [];

  documents: any[] = [];

  selectedData: Contracts | undefined;

  showDocumentDetailsModal: boolean = false;

  newAttachments: FilePicked[] = [];
  loading: boolean = false;
  documentsLoading: boolean = false;
  showAttachmentModal: boolean = false;

  canAdd: boolean = false;
  role: string = '';
  isCompliance: boolean = false;
  isMaster: boolean = false;
  canManageComplianceDocuments: boolean = false;

  eventMainVersion: any;
  eventComparingVersion: any;

  deletingDocumentId: number | null = null;
  showDeleteModal: boolean = false;
  documentToDelete: any = null;

  // Modal de visualização de documentos
  showDocumentModal: boolean = false;
  selectedDocument: any = null;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    this.canAdd = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_documents_add`,
    );

    const user = this.authService.getUser();
    this.isCompliance =
      user?.active_register?.register?.department?.name
        .toLowerCase()
        .includes('compliance') || false;
    this.isMaster =
      this.storageService.getPermissionType() === 'master' ||
      this.storageService.getPermissionType() === 'owner';
    this.canManageComplianceDocuments = this.isCompliance || this.isMaster;

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      // Usar API v3 para carregar documentos
      this.documentsLoading = true;
      this.loadDocumentsV3();
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      },
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange
    ) {
      const previousValue = changes['comparingVersions'].previousValue;
      const currentValue = changes['comparingVersions'].currentValue;

      if (previousValue === true && currentValue === false) {
        // Usar API v3 para carregar documentos
        this.documentsLoading = true;
        this.loadDocumentsV3();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    this.mainData = [];
    this.comparingData = [];
    this.mainDocuments = [];
    this.mainDocumentsRaw = [];
    this.comparingDocuments = [];
    this.comparingDocumentsRaw = [];
    this.mainLoading = false;
    this.comparingLoading = false;

    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  compareData() {
    if (!this.mainData || !this.comparingData) {
      return;
    }

    const processedData = this.comparingData.map((comparingItem) => {
      const referenceItem = this.mainData.find(
        (ref) => ref.id === comparingItem.id,
      );

      if (!referenceItem) {
        return {
          ...comparingItem,
          compareClass: 'row-compare-added',
        };
      }

      const statusChanged = referenceItem.status !== comparingItem.status;
      const witnessChanged =
        JSON.stringify(referenceItem.witness) !==
        JSON.stringify(comparingItem.witness);
      const nameChanged = referenceItem.name !== comparingItem.name;

      if (statusChanged || witnessChanged || nameChanged) {
        return {
          ...comparingItem,
          compareClass: 'row-compare-modified',
          changedFields: {
            status: statusChanged,
            witness: witnessChanged,
            name: nameChanged,
          },
        };
      }

      return {
        ...comparingItem,
        compareClass: '',
      };
    });

    this.mainData.forEach((referenceItem) => {
      const existsInComparing = this.comparingData.find(
        (comp) => comp.id === referenceItem.id,
      );
      if (!existsInComparing) {
        processedData.push({
          ...referenceItem,
          compareClass: 'row-compare-removed',
        });
      }
    });

    this.comparingData = processedData;
  }

  compareDocumentsData() {
    const mainDocsSource = this.mainDocumentsRaw || [];
    const comparingDocsSource = this.comparingDocumentsRaw || [];

    if (!mainDocsSource || !comparingDocsSource) {
      return;
    }

    const sameVersionSelected =
      this.approvalService.approvalData.mainVersion &&
      this.approvalService.approvalData.comparingVersion &&
      this.approvalService.approvalData.mainVersion ===
        this.approvalService.approvalData.comparingVersion;

    if (sameVersionSelected) {
      this.comparingDocuments = (comparingDocsSource || []).map((doc: any) => ({
        ...doc,
        compareClass: '',
        data: (doc.data || []).map((item: any) => ({
          ...item,
          compareClass: '',
          changedFields: {},
        })),
      }));
      return;
    }

    const processedDocuments = comparingDocsSource.map((comparingDoc) => {
      const referenceDoc = mainDocsSource.find(
        (ref) => ref.slug === comparingDoc.slug,
      );

      if (!referenceDoc) {
        return {
          ...comparingDoc,
          compareClass: 'row-compare-added',
          data: comparingDoc.data.map((item: any) => ({
            ...item,
            compareClass: 'row-compare-added',
          })),
        };
      }

      const processedData = comparingDoc.data.map((comparingItem: any) => {
        const referenceItem = referenceDoc.data.find((refItem: any) => {
          if (refItem.id && comparingItem.id) {
            return refItem.id === comparingItem.id;
          }
          return refItem.created_at === comparingItem.created_at;
        });

        if (!referenceItem) {
          return {
            ...comparingItem,
            compareClass: 'row-compare-added',
          };
        }

        const fileChanged =
          this.normalizeFilePath(referenceItem.file) !==
          this.normalizeFilePath(comparingItem.file);
        const dateChanged =
          referenceItem.created_at !== comparingItem.created_at;

        if (fileChanged || dateChanged) {
          return {
            ...comparingItem,
            compareClass: 'row-compare-modified',
            changedFields: {
              file: fileChanged,
              created_at: dateChanged,
            },
          };
        }

        return {
          ...comparingItem,
          compareClass: '',
        };
      });

      referenceDoc.data.forEach((referenceItem: any) => {
        const existsInComparing = comparingDoc.data.find((compItem: any) => {
          if (referenceItem.id && compItem.id) {
            return compItem.id === referenceItem.id;
          }
          return compItem.created_at === referenceItem.created_at;
        });
        if (!existsInComparing) {
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
          });
        }
      });

      const isDocumentModified = processedData.some(
        (item: any) =>
          item.compareClass === 'row-compare-modified' ||
          item.compareClass === 'row-compare-added' ||
          item.compareClass === 'row-compare-removed',
      );

      return {
        ...comparingDoc,
        compareClass: isDocumentModified ? 'row-compare-modified' : '',
        data: processedData,
      };
    });

    mainDocsSource.forEach((referenceDoc) => {
      const existsInComparing = comparingDocsSource.find(
        (comp) => comp.slug === referenceDoc.slug,
      );
      if (!existsInComparing) {
        processedDocuments.push({
          ...referenceDoc,
          compareClass: 'row-compare-removed',
          data: referenceDoc.data.map((item: any) => ({
            ...item,
            compareClass: 'row-compare-removed',
          })),
        });
      }
    });

    this.comparingDocuments = processedDocuments;
  }

  private normalizeFilePath(file: string | null | undefined): string {
    if (!file) return '';
    const [withoutQuery] = String(file).split('?');
    return withoutQuery.trim().toLowerCase();
  }

  isFieldChanged(item: any, field: string): boolean {
    return item?.changedFields?.[field] === true;
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = [];
      this.mainDocuments = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.handleGetDocuments('main').then(() => {
      // Recalcular comparação local quando o lado esquerdo muda,
      // sem refazer chamada da versão da direita.
      if (
        this.comparingVersions &&
        this.approvalService.approvalData.comparingVersion
      ) {
        this.compareData();
        this.compareDocumentsData();
        this.cdr.detectChanges();
      }
    });
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = [];
      this.comparingDocuments = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.handleGetDocuments('comparing').then(() => {
      this.compareData();
      this.compareDocumentsData();
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    });
  }

  async getCompleteFileUploadDocuments(): Promise<any[]> {
    const registerUuid = this.route.snapshot.params.id;

    try {
      const completeFileRes = await this.api.get({
        route: 'api/registration/complete_file_upload',
        params: {
          register_id: registerUuid,
        },
        token: true,
      });

      let allDocuments: any[] = [];

      if (completeFileRes && completeFileRes.get_info) {
        if (Array.isArray(completeFileRes.get_info)) {
          allDocuments = completeFileRes.get_info;
        } else {
          allDocuments = [];
        }
      } else if (Array.isArray(completeFileRes)) {
        allDocuments = completeFileRes;
      } else if (completeFileRes && completeFileRes.data !== undefined) {
        if (Array.isArray(completeFileRes.data)) {
          allDocuments = completeFileRes.data;
        } else if (completeFileRes.data === '-' || !completeFileRes.data) {
          allDocuments = [];
        } else {
          allDocuments = [completeFileRes.data];
        }
      }

      allDocuments = allDocuments
        .filter((item) => {
          if (!item || item === null || item === undefined) {
            return false;
          }
          return item.file !== undefined && item.file !== null;
        })
        .map((item) => {
          return {
            ...item,
            created_at:
              item.date_created ||
              item.created_at ||
              item.created ||
              item.timestamp ||
              null,
            file: item.file || null,
          };
        });

      return allDocuments;
    } catch (error) {
      console.error(
        'Erro ao buscar documentos do complete_file_upload:',
        error,
      );
      return [];
    }
  }

  async handleGetDocuments(versionType: 'main' | 'comparing' | null = null) {
    // Se estiver em modo de comparação, usar API v3
    if (this.comparingVersions && versionType) {
      await this.loadDocumentsV3ForComparison(versionType);
      return;
    }

    // Modo normal - usar API v3
    if (!versionType) {
      await this.loadDocumentsV3();
      return;
    }

    // Fallback para código antigo (não deveria chegar aqui)
    let documents_slugs = [
      'us-person-declaration',
      'suitability_pf',
      'suitability_pj',
      'declaration_investor',
      'registration_form',
      'refusal_term',
      'disqualification',
    ];

    let approvalData = this.approval;
    if (versionType) {
      approvalData = await this.getApprovalData(versionType);
      if (!approvalData) {
        if (versionType === 'main') {
          this.mainDocuments = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingDocuments = [];
          this.comparingLoading = false;
        }
        this.cdr.detectChanges();
        return;
      }
    }

    if (approvalData?.register?.role?.slug?.includes('PJ')) {
      documents_slugs = documents_slugs.filter(
        (slug) => slug !== 'suitability_pf',
      );
    } else {
      documents_slugs = documents_slugs.filter(
        (slug) => slug !== 'suitability_pj',
      );
    }

    const documentsPromises = documents_slugs.map((slug) =>
      this.getDocuments(slug, versionType, approvalData),
    );

    const documentsResults = await Promise.all(documentsPromises);

    const documentsArray = documents_slugs
      .map((slug, index) => {
        const doc = documentsResults[index];
        if (doc === null || doc === undefined) {
          return {
            slug: slug,
            data: [],
          };
        }
        return doc;
      })
      .filter((doc) => {
        return (
          doc !== null &&
          doc !== undefined &&
          doc.slug &&
          Array.isArray(doc.data)
        );
      });

    // Removido: não listar mais documentos da API complete_file_upload na aba Documentos

    // Filtra documentos baseado em permissões
    const filteredDocuments = documentsArray.filter((doc) => {
      // Documentos de compliance só aparecem para compliance ou master
      if (doc.slug === 'compliance-documents') {
        return this.canManageComplianceDocuments;
      }

      // Documentos normais (US Person, Suitability, etc.) só aparecem para shareholder
      const normalDocuments = [
        'us-person-declaration',
        'suitability_pf',
        'suitability_pj',
        'declaration_investor',
        'registration_form',
        'refusal_term',
        'disqualification',
      ];

      if (normalDocuments.includes(doc.slug)) {
        return this.role === 'shareholder';
      }

      return true;
    });

    if (versionType === 'main') {
      this.mainDocuments = filteredDocuments;
      this.mainLoading = false;
    } else if (versionType === 'comparing') {
      this.comparingDocuments = filteredDocuments;
      this.comparingLoading = false;
    } else {
      this.documents = [...filteredDocuments];
      this.documentsLoading = false;
    }
    this.cdr.detectChanges();
  }

  shouldShowEmptyDocument(slug: string): boolean {
    const documentsToShowEmpty = [
      'us-person-declaration',
      'suitability_pf',
      'suitability_pj',
      'declaration_investor',
      'registration_form',
      'refusal_term',
      'disqualification',
    ];
    return documentsToShowEmpty.includes(slug);
  }

  hasDocumentsWithData(documents: any[]): boolean {
    if (!documents || documents.length === 0) {
      return false;
    }
    // Retorna true se houver documentos na lista (mesmo que vazios)
    // Isso permite exibir a tabela com os documentos normais mesmo sem arquivos anexados
    return documents.length > 0;
  }

  async getApprovalData(versionType: 'main' | 'comparing'): Promise<any> {
    // Não chamar APIs v2 - apenas verificar cache
    // Se não houver dados, retornar null
    return null;

    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'documents',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else {
        return null;
      }

      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      return data[0];
    } catch (error) {
      return null;
    }
    */
  }

  async getDocuments(
    slug: string,
    versionType: 'main' | 'comparing' | null = null,
    approvalData: any = null,
  ) {
    const approval = approvalData || this.approval;
    if (!approval) return null;

    const registerUuid = this.route.snapshot.params.id;

    const req_params: any = {
      register: registerUuid,
      tab: 'documents',
      type: slug,
    };

    if (
      versionType === 'main' &&
      this.approvalService.approvalData.mainVersion
    ) {
      req_params['register'] = registerUuid;
      req_params['version'] = this.approvalService.approvalData.mainVersion;
    } else if (
      versionType === 'comparing' &&
      this.approvalService.approvalData.comparingVersion
    ) {
      req_params['register'] = registerUuid;
      req_params['version'] =
        this.approvalService.approvalData.comparingVersion;
    }

    // Não chamar APIs v2 - retornar vazio
    return {
      slug,
      data: [],
    };

    /* Código antigo comentado - não chamar APIs v2
    try {
      const res = await this.api.get({
        route: 'api/v2/approvals/version/',
        params: req_params,
        token: true,
      });

      let data: any[] = [];

      if (Array.isArray(res)) {
        const firstItem = res[0];
        if (firstItem && firstItem.register && firstItem.register.documents) {
          res.forEach((approvalItem: any) => {
            if (
              approvalItem.register &&
              approvalItem.register.documents &&
              Array.isArray(approvalItem.register.documents)
            ) {
              data = data.concat(approvalItem.register.documents);
            }
          });
        } else {
          data = res;
        }
      } else if (res && res.data !== undefined) {
        if (Array.isArray(res.data)) {
          data = res.data;
        } else if (res.data === '-' || !res.data) {
          data = [];
        } else {
          data = [res.data];
        }
      } else if (res && res.register && res.register.documents) {
        if (Array.isArray(res.register.documents)) {
          data = res.register.documents;
        } else {
          data = [];
        }
      } else {
        data = [];
      }

      data = data.filter((item) => {
        if (!item || item === null || item === undefined) return false;

        if (item.register && !item.file) {
          return false;
        }

        return item.file !== undefined || item.created_at !== undefined;
      });

      return {
        slug,
        data: data,
      };
    } catch (error) {
      return {
        slug,
        data: [],
      };
    }
    */
  }

  toggleAttachmentModal() {
    this.clearAttachments = !this.showAttachmentModal;
    this.showAttachmentModal = !this.showAttachmentModal;
    this.newAttachments = [];
  }

  toggleDocumentDetailsModal() {
    this.showDocumentDetailsModal = !this.showDocumentDetailsModal;
    this.selectedData = undefined;
  }

  getDocumentLabel(slug: string) {
    switch (slug) {
      case 'us-person-declaration':
        return 'Declaração US Person (FATCA)';

      case 'suitability_pf':
        return 'Suitability';

      case 'suitability_pj':
        return 'Suitability';

      case 'declaration_investor':
        return 'Declaração de Investidor';

      case 'refusal_term':
        return 'Termo de Recusa';

      case 'registration_form':
        return 'Ficha cadastral';

      case 'disqualification':
        return 'Termo de Desenquadramento';

      case 'compliance-documents':
        return 'Documentos de Compliance';

      default:
        return slug;
    }
  }

  openUsPerson(file: string) {
    // Abrir PDF no modal ao invés de nova guia
    this.selectedDocument = file;
    this.showDocumentModal = true;
  }

  closeDocumentModal() {
    this.showDocumentModal = false;
    this.selectedDocument = null;
  }

  formateDate(date: string) {
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return `${day} às ${hours}`;
  }

  getAttachmentDisabled() {
    return this.newAttachments.length === 0;
  }

  handleOpenVisualizer(id: number) {
    this.showDocumentDetailsModal = true;

    this.selectedData = this.data.find((doc) => doc.id === id);
  }

  formatWitnesses(witnesses: Witness[]) {
    const formatNames = witnesses.map((witness) => witness.name);

    return formatNames.join(', ');
  }

  setFiles(files: FilePicked[]) {
    this.newAttachments = [...files];
  }

  async uploadAttachment() {
    if (this.newAttachments.length === 0) {
      this.toast.show('info', 'Atenção', 'Selecione um arquivo para anexar');
      return;
    }

    this.loading = true;

    try {
      const file = this.newAttachments[0].file;
      const registerUuid = this.route.snapshot.params.id;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', '51');

      if (registerUuid) {
        formData.append('register', registerUuid);
      }

      // Adicionar version_number e department apenas para compliance ou master
      if (this.canManageComplianceDocuments) {
        // Obter o ID da versão
        const versionNumber =
          this.approvalService.approvalData?.version ||
          this.approval?.version ||
          this.approval?.register?.version ||
          null;

        if (versionNumber) {
          formData.append('version_number', versionNumber.toString());
        }

        // Obter o nome do departamento
        const user = this.authService.getUser();
        const departmentName =
          user?.active_register?.register?.department?.name || null;

        if (departmentName) {
          formData.append('department', departmentName);
        }
      }

      await this.api.post({
        route: 'api/registration/complete_file_upload',
        body: formData,
        token: true,
        noDefautHeaders: true,
      });

      this.toast.show('info', 'Sucesso', 'Documento anexado com sucesso!');

      this.toggleAttachmentModal();

      this.documentsLoading = true;
      this.handleGetDocuments();
    } catch (error: any) {
      console.error('Erro ao anexar documento:', error);
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao anexar documento',
      );
    } finally {
      this.loading = false;
    }
  }

  async downloadComplianceDocument(item: any) {
    if (!item.file) {
      this.toast.show('error', 'Erro', 'Arquivo não disponível para download');
      return;
    }

    try {
      // Extrai o nome do arquivo primeiro
      let fileName = 'documento.pdf';
      try {
        const urlParts = item.file.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        fileName = lastPart.split('?')[0] || 'documento.pdf';
      } catch (e) {
        // Ignora erro na extração do nome
      }

      // Para URLs assinadas do Google Cloud Storage, precisamos fazer fetch
      // e criar um blob local para forçar o download (o atributo download
      // não funciona com URLs cross-origin)
      try {
        const response = await fetch(item.file, {
          method: 'GET',
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error('Arquivo vazio recebido');
        }

        // Cria um blob URL local para fazer o download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();

        // Limpa após o download
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (fetchError) {
        // Fallback: se fetch falhar (CORS), abre em nova aba
        const link = document.createElement('a');
        link.href = item.file;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    } catch (error: any) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message ||
          error?.message ||
          'Erro ao fazer download do arquivo',
      );
    }
  }

  deleteComplianceDocument(item: any) {
    if (!item.id) {
      this.toast.show('error', 'Erro', 'ID do documento não encontrado');
      return;
    }

    if (this.deletingDocumentId === item.id) {
      return;
    }

    this.documentToDelete = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.documentToDelete = null;
  }

  async confirmDeleteDocument() {
    if (!this.documentToDelete || !this.documentToDelete.id) {
      return;
    }

    this.deletingDocumentId = this.documentToDelete.id;
    this.loading = true;

    try {
      const registerUuid = this.route.snapshot.params.id;

      const url = this.api.getUrl({
        route: 'api/registration/complete_file_upload',
        params: {
          register_id: registerUuid,
          file_id: this.documentToDelete.id,
        },
      });

      const headers = this.api.getHeaders(true, false);
      const httpHeaders = new HttpHeaders(headers);

      await this.http
        .delete(url, {
          headers: httpHeaders,
        })
        .toPromise();

      this.toast.show('info', 'Sucesso', 'Documento excluído com sucesso!');

      this.documentsLoading = true;
      this.handleGetDocuments();
    } catch (error: any) {
      console.error('Erro ao excluir documento:', error);
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao excluir documento',
      );
    } finally {
      this.loading = false;
      this.deletingDocumentId = null;
      this.closeDeleteModal();
    }
  }

  getDeleteModalTitle() {
    return 'Confirmar exclusão';
  }

  getDeleteModalMessage() {
    return 'Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.';
  }

  getDeleteConfirmText() {
    return 'Sim, excluir';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  /**
   * Carrega documentos usando API v3 para comparação de versões
   */
  async loadDocumentsV3ForComparison(versionType: 'main' | 'comparing') {
    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        console.warn('Register UUID não encontrado');
        if (versionType === 'main') {
          this.mainDocuments = [];
          this.mainLoading = false;
        } else {
          this.comparingDocuments = [];
          this.comparingLoading = false;
        }
        return;
      }

      // Obter versão a ser usada
      let versionToUse = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      }

      if (!versionToUse) {
        if (versionType === 'main') {
          this.mainDocuments = [];
          this.mainLoading = false;
        } else {
          this.comparingDocuments = [];
          this.comparingLoading = false;
        }
        return;
      }

      // Preparar parâmetros para API v3
      const baseParams: any = {
        data_type: 'documents',
        version: versionToUse,
      };

      // Fazer chamada para API v3 com document_category: 'all'
      const allResponse = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: { ...baseParams, document_category: 'all' },
        token: true,
      });

      // Processar resposta da API
      if (allResponse?.data?.versions?.documents?.versions?.[0]?.data) {
        const documentsData =
          allResponse.data.versions.documents.versions[0].data;

        // Agrupar por type.slug mantendo TODOS os documentos de cada tipo
        const documentsByType = new Map<string, any[]>();

        documentsData.forEach((doc: any) => {
          if (!doc.type?.slug) return;

          const slug = doc.type.slug;
          if (!documentsByType.has(slug)) {
            documentsByType.set(slug, []);
          }
          documentsByType.get(slug)!.push(doc);
        });

        // Transformar para o formato esperado pelo template
        const processedDocuments: any[] = [];

        documentsByType.forEach((docs, slug) => {
          // Filtrar apenas docs com file (exceto registration_form)
          const docsWithFile = docs.filter((doc: any) => !!doc.file);
          const docsToShow = slug === 'registration_form'
            ? (docsWithFile.length > 0 ? docsWithFile : docs)
            : docsWithFile;

          processedDocuments.push({
            slug: slug,
            data: docsToShow.map((doc: any) => ({
              ...doc,
              file: doc.file || null,
              created_at: doc.created_at,
            })),
          });
        });

        // Ordenar documentos por ordem de exibição esperada
        const order = [
          'us-person-declaration',
          'suitability_pf',
          'suitability_pj',
          'declaration_investor',
          'registration_form',
          'refusal_term',
          'disqualification',
        ];

        processedDocuments.sort((a, b) => {
          const indexA = order.indexOf(a.slug);
          const indexB = order.indexOf(b.slug);

          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        });

        // Filtra documentos baseado em permissões
        const filteredDocuments = processedDocuments.filter((doc) => {
          // Documentos de compliance só aparecem para compliance ou master
          if (doc.slug === 'compliance-documents') {
            return this.canManageComplianceDocuments;
          }

          // Documentos normais (US Person, Suitability, etc.) só aparecem para shareholder
          const normalDocuments = [
            'us-person-declaration',
            'suitability_pf',
            'suitability_pj',
            'declaration_investor',
            'registration_form',
            'refusal_term',
            'disqualification',
          ];

          if (normalDocuments.includes(doc.slug)) {
            return this.role === 'shareholder';
          }

          return true;
        });

        // Atualizar documentos baseado no tipo de versão
        if (versionType === 'main') {
          this.mainDocumentsRaw = filteredDocuments.map((doc: any) => ({
            ...doc,
            data: (doc.data || []).map((item: any) => ({ ...item })),
          }));
          this.mainDocuments = this.mainDocumentsRaw.map((doc: any) => ({
            ...doc,
            data: (doc.data || []).map((item: any) => ({ ...item })),
          }));
          this.mainLoading = false;
        } else {
          this.comparingDocumentsRaw = filteredDocuments.map((doc: any) => ({
            ...doc,
            data: (doc.data || []).map((item: any) => ({ ...item })),
          }));
          this.comparingDocuments = this.comparingDocumentsRaw.map((doc: any) => ({
            ...doc,
            data: (doc.data || []).map((item: any) => ({ ...item })),
          }));
          this.comparingLoading = false;
        }
      } else {
        // Se não houver dados, manter documentos vazios
        if (versionType === 'main') {
          this.mainDocuments = [];
          this.mainDocumentsRaw = [];
          this.mainLoading = false;
        } else {
          this.comparingDocuments = [];
          this.comparingDocumentsRaw = [];
          this.comparingLoading = false;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar documentos v3 para comparação:', error);
      if (versionType === 'main') {
        this.mainDocuments = [];
        this.mainDocumentsRaw = [];
        this.mainLoading = false;
      } else {
        this.comparingDocuments = [];
        this.comparingDocumentsRaw = [];
        this.comparingLoading = false;
      }
    }
  }

  /**
   * Carrega documentos usando API v3
   * Por enquanto apenas chama a API, sem listar os dados
   */
  async loadDocumentsV3() {
    this.documentsLoading = true;

    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        console.warn('Register UUID não encontrado');
        this.documentsLoading = false;
        return;
      }

      // Preparar parâmetros base para API v3
      const baseParams: any = {
        data_type: 'documents',
      };

      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        baseParams.version_number = versionToUse;
      } else {
        baseParams.last = true;
      }

      // Fazer 5 chamadas paralelas para API v3: suitability, declarations, registration, terms e all
      const [
        suitabilityResponse,
        declarationsResponse,
        registrationResponse,
        termsResponse,
        allResponse,
      ] = await Promise.all([
        // 1. Suitability
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: { ...baseParams, document_category: 'suitability' },
          token: true,
        }),
        // 2. Declarations
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: { ...baseParams, document_category: 'declarations' },
          token: true,
        }),
        // 3. Registration Form
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: { ...baseParams, document_category: 'registration' },
          token: true,
        }),
        // 4. Terms (Refusal e Disqualification)
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: { ...baseParams, document_category: 'terms' },
          token: true,
        }),
        // 5. All (todos os documentos)
        this.api.get<any>({
          route: `api/v3/registration/versions/register/${register_uuid}/`,
          params: { ...baseParams, document_category: 'all' },
          token: true,
        }),
      ]);

      // Processar resposta da API 'all' para exibir documentos (exceto disqualification)
      if (allResponse?.data?.versions?.documents?.versions?.[0]?.data) {
        const documentsData =
          allResponse.data.versions.documents.versions[0].data;

        // Agrupar por type.slug — para a maioria dos tipos, manter apenas 1 (melhor) doc por slug
        // 'disqualification' é tratado separadamente via termsResponse (que contém slug_name)
        const documentsByType = new Map<string, any>();

        documentsData.forEach((doc: any) => {
          if (!doc.type?.slug) return;
          const slug = doc.type.slug;

          // disqualification será tratado via termsResponse
          if (slug === 'disqualification') return;

          const existing = documentsByType.get(slug);
          if (!existing) {
            documentsByType.set(slug, doc);
          } else {
            const docHasFile = !!doc.file;
            const existingHasFile = !!existing.file;
            if (docHasFile && !existingHasFile) {
              documentsByType.set(slug, doc);
            } else if (docHasFile && existingHasFile) {
              if (new Date(doc.created_at) > new Date(existing.created_at)) {
                documentsByType.set(slug, doc);
              }
            } else if (!docHasFile && !existingHasFile) {
              if (new Date(doc.created_at) > new Date(existing.created_at)) {
                documentsByType.set(slug, doc);
              }
            }
          }
        });

        // Transformar para o formato esperado pelo template
        const processedDocuments: any[] = [];

        documentsByType.forEach((doc, slug) => {
          const filePath = doc.file || null;
          const shouldInclude = slug === 'registration_form' || filePath;
          processedDocuments.push({
            slug: slug,
            data: shouldInclude
              ? [{ ...doc, file: filePath, created_at: doc.created_at }]
              : [],
          });
        });

        // Adicionar disqualification a partir do termsResponse (tem slug_name, e pode ter múltiplos)
        if (termsResponse?.data?.versions?.documents?.versions?.[0]?.data) {
          const termsData = termsResponse.data.versions.documents.versions[0].data;
          const disqualDocs = termsData
            .filter((doc: any) => doc.type?.slug === 'disqualification' && !!doc.file)
            .map((doc: any) => ({ ...doc, file: doc.file || null, created_at: doc.created_at }));
          if (disqualDocs.length > 0) {
            processedDocuments.push({ slug: 'disqualification', data: disqualDocs });
          }
        }

        // Ordenar documentos por ordem de exibição esperada
        const order = [
          'us-person-declaration',
          'suitability_pf',
          'suitability_pj',
          'declaration_investor',
          'registration_form',
          'refusal_term',
          'disqualification',
        ];

        processedDocuments.sort((a, b) => {
          const indexA = order.indexOf(a.slug);
          const indexB = order.indexOf(b.slug);

          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        });

        // Atualizar documentos
        this.documents = processedDocuments;
        this.documentsLoading = false;
      } else {
        // Se não houver dados, manter documentos vazios
        this.documents = [];
        this.documentsLoading = false;
      }
    } catch (error) {
      console.error('Erro ao carregar documentos v3:', error);
      this.documents = [];
      this.documentsLoading = false;
    }
  }
}
